import React from 'react';
import axios from 'axios';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { mdiCloseCircleOutline, mdiEyeOutline } from '@mdi/js';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import Badge from '@/components/layout/Badge';
import Skeleton from '@/components/layout/Skeleton';
import AlertBox from '@/components/layout/AlertBox';
import Modal from '@/components/layout/Modal';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { formatarMoeda } from '@tera/shared/src/types/Catalogo';
import DetalheOrcamento from './DetalheOrcamento';
import { CANCELAVEL, ORIGEM_LABEL, STATUS_BADGE, STATUS_LABEL, formatarData } from './constantes';
import styles from './page.module.css';

const Page = () => {
    const [status, setStatus] = React.useState<string | null>(null);
    const [detalheId, setDetalheId] = React.useState<number | null>(null);

    const queryClient = useQueryClient();

    const queryList = useInfiniteQuery({
        queryKey: ['orcamento', status],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({ t: '50' });
            if (status) params.set('status', status);
            if (pageParam) params.set('c', pageParam);
            return await axios.get('/api/orcamento', { params });
        },
        getNextPageParam: (lastPage) => lastPage.data.cursor ?? undefined,
    });

    const cancelarMutation = useMutation(
        async (id: number) => axios.post(`/api/orcamento/${id}/cancelar`),
        {
            onSuccess: () => {
                toast('Orçamento cancelado', { type: 'success', position: 'top-center' });
                queryClient.invalidateQueries(['orcamento']);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao cancelar orçamento', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const cabecalhos: DataTableHeader[] = [
        { label: 'ID', width: '60px', column: 'id' },
        { label: 'Catálogo', width: '18%', render: (l: any) => l.catalogo?.nome ?? '—' },
        { label: 'Cliente', width: '18%', render: (l: any) => l.cliente?.nome ?? '—' },
        {
            label: 'Status',
            width: '12%',
            render: (l: any) => (
                <Badge variant={STATUS_BADGE[l.status] ?? 'secondary'}>
                    {STATUS_LABEL[l.status] ?? l.status}
                </Badge>
            ),
        },
        { label: 'Origem', width: '14%', render: (l: any) => ORIGEM_LABEL[l.origem] ?? l.origem },
        {
            label: 'Total',
            width: '12%',
            alignRight: true,
            render: (l: any) => formatarMoeda(l.total, l.moeda),
        },
        { label: 'Emitido em', width: '14%', render: (l: any) => formatarData(l.enviado_em) },
        {
            label: 'Ações',
            width: '110px',
            alignRight: true,
            render: (l: any) => (
                <HBox gap={8} style={{ justifyContent: 'flex-end' }}>
                    <Botao
                        variant="none-primary"
                        title="Ver detalhes"
                        icon={mdiEyeOutline}
                        onClick={() => setDetalheId(l.id)}
                    />
                    {CANCELAVEL.includes(l.status) && (
                        <BotaoPerigoso
                            buttonProps={{ title: 'Cancelar orçamento' }}
                            variant="none-danger"
                            icon={mdiCloseCircleOutline}
                            confirmation="Tem certeza que deseja cancelar este orçamento? O cliente não poderá mais aceitá-lo."
                            onClick={() => cancelarMutation.mutate(l.id)}
                        />
                    )}
                </HBox>
            ),
        },
    ];

    const linhas = queryList.data?.pages?.map((p) => p.data.result).flat() ?? [];
    const total = queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    const renderConteudo = () => {
        if (queryList.error) {
            return (
                <AlertBox variant="danger">
                    <p>
                        Erro ao carregar orçamentos:{' '}
                        {(queryList.error as any)?.message || 'Erro desconhecido'}
                    </p>
                </AlertBox>
            );
        }

        if (queryList.isLoading) {
            return (
                <VBox gap={2}>
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                </VBox>
            );
        }

        return (
            <VBox gap={8}>
                <Descricao>
                    Exibindo {linhas.length} de {total} resultado(s).
                </Descricao>
                <DataTable
                    isLoading={queryList.isFetching}
                    cabecalhos={cabecalhos}
                    linhas={linhas}
                    hasMoreData={queryList.hasNextPage}
                    onLoadMore={async () => queryList.fetchNextPage()}
                />
            </VBox>
        );
    };

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap={24}>
                    <HBox>
                        <div className={styles.headerTitulo}>
                            <Titulo>Orçamentos</Titulo>
                            <Descricao>
                                Orçamentos emitidos pela IA, fluxos, formulários ou manualmente a
                                partir de um catálogo. Os valores exibidos são os calculados e
                                gravados no momento da emissão.
                            </Descricao>
                        </div>
                    </HBox>

                    <HBox className={styles.filtros}>
                        <CampoSeletor
                            instanceId="orcamento-status-filtro"
                            label=""
                            placeholder="Todos os status"
                            nullable
                            noSearch
                            value={status}
                            onChange={setStatus}
                            options={Object.entries(STATUS_LABEL).map(([id, label]) => ({
                                id,
                                label,
                            }))}
                        />
                    </HBox>

                    {renderConteudo()}
                </VBox>
            </div>

            <Modal
                instanceId="orcamento-detalhe-modal"
                show={!!detalheId}
                size="lg"
                title={`Orçamento #${detalheId ?? ''}`}
                onClose={() => setDetalheId(null)}
                onCancel={() => setDetalheId(null)}
                hideControls
            >
                {detalheId && <DetalheOrcamento id={detalheId} />}
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
